import { useActionCable } from "common/hooks/useActionCable";
import { usePrevious } from "@design-lab/shared/hooks/usePrevious";
import { useQuery } from "@tanstack/react-query";
import { objNamesToCamelCase, objNamesToSnakeCase } from "common/utils/helpers/cases";
import { track } from "common/utils/mixpanel/tracker";
import type { PropsWithChildren } from "react";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import { api, models } from "jiffy-api";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";
import { useExperiments } from "../lib/config";

const RESULT_DATA_ACTIONS = [
  "upload_artwork",
  "validate_artwork",
  "process_artwork",
  "extract_bg_metadata",
  "fetch_price",
  "fetch_delivery_date"
] as const;

function isTrackedAction(
  message: models.cable.DtfChannel.Actions
): message is
  | models.cable.DtfChannel.uploadArtwork.Action
  | models.cable.DtfChannel.validateArtwork.Action
  | models.cable.DtfChannel.processArtwork.Action
  | models.cable.DtfChannel.extractBgMetadata.Action
  | models.cable.DtfChannel.fetchPrice.Action
  | models.cable.DtfChannel.fetchDeliveryDate.Action {
  return RESULT_DATA_ACTIONS.includes(message.action as any);
}

function useDtfChannelCreator() {
  const experiments = useExperiments();
  const wsMessages = useRef<Record<number, models.cable.DtfChannel.StoredMessage>>({});

  const [searchParams] = useSearchParams();
  const enhancementsStarted = searchParams.get("enhancements_started") === "true";
  const urlChannelUuid = searchParams.get("dtf_channel_uuid");

  const { data } = useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["dtf-init-config"],
    retry: true,
    queryFn: async () => {
      // If we have enhancements_started with channel UUID, return it directly instead of API call
      if (enhancementsStarted && urlChannelUuid) {
        return { dtfChannelUuid: urlChannelUuid };
      }

      // Otherwise make the API call for normal flow
      return api.dtf.getInitConfig({ catalog_number: "DTF001" }).then(objNamesToCamelCase);
    }
  });

  const channelUuid = data?.dtfChannelUuid;

  const onMessageReceived = useCallback((message: models.cable.DtfChannel.Actions) => {
    if (message && typeof message === "object" && "action" in message) {
      const eventProperties: Record<string, unknown> = {
        action: message.action,
        channel: "DtfChannel"
      };

      if ("result" in message && message.result && typeof message.result === "object") {
        if ("artworkId" in message.result) {
          eventProperties["artwork_id"] = message.result["artworkId"];
        }
        if ("success" in message.result) {
          eventProperties["success"] = message.result["success"];
        }
      }

      track("websocket_message", eventProperties);
    }
  }, []);

  const messageFilter = useCallback((message: models.cable.DtfChannel.Actions) => {
    // Skip progress events to prevent overwriting important events
    if (
      message &&
      typeof message === "object" &&
      "action" in message &&
      message.action === "extract_bg_metadata_progress"
    ) {
      return false;
    }
    return true;
  }, []);

  const actionCableOptions = useMemo(
    () => ({
      experiments: experiments as Record<string, unknown>,
      onMessageReceived,
      messageFilter
    }),
    [experiments, onMessageReceived, messageFilter]
  );

  const { lastMessage, perform, isConnected } = useActionCable(
    channelUuid,
    "DtfChannel",
    actionCableOptions
  );

  const previousIsConnected = usePrevious(isConnected);

  useEffect(() => {
    if (!lastMessage || !isTrackedAction(lastMessage)) return;

    if ("data" in lastMessage) {
      wsMessages.current[lastMessage.data.artworkId] = {
        action: lastMessage.action,
        result: lastMessage.data,
        data: lastMessage.data
      };
    } else if ("result" in lastMessage) {
      const messageResult =
        "processData" in lastMessage.result ? lastMessage.result.processData : undefined;

      wsMessages.current[lastMessage.result.artworkId] = {
        action: lastMessage.action,
        result: messageResult,
        data: lastMessage.result.data
      };
    }
  }, [lastMessage]);

  useEffect(() => {
    if (!previousIsConnected && isConnected) {
      const processTimeStamp = Date.now();

      Object.values(wsMessages.current).forEach(
        (message: models.cable.DtfChannel.StoredMessage) => {
          perform({
            command: "continue_dtf_artwork_process",
            data: {
              lastProcess: message.action,
              lastProcessData: objNamesToSnakeCase(message.result),
              data: objNamesToSnakeCase({ ...message.data, processTimeStamp })
            }
          });
        }
      );
    }
  }, [isConnected, perform, previousIsConnected]);

  return {
    initConfig: data,
    channel: {
      retrieveDtfChannelUuid: async () => channelUuid,
      lastMessage,
      isConnected
    }
  };
}

type DtfChannelCreatorValue = ReturnType<typeof useDtfChannelCreator>;
type DtfInitConfig = DtfChannelCreatorValue["initConfig"];
type DtfChannel = DtfChannelCreatorValue["channel"];

export const dtfInitConfigAtom = atom<DtfInitConfig | null>(null);
export const dtfChannelAtom = atom<DtfChannel | null>(null);

export function DtfChannelProvider({ children }: PropsWithChildren) {
  const value = useDtfChannelCreator();

  // Initial hydration (runs once synchronously during first render)
  useHydrateAtoms([
    [dtfInitConfigAtom, value.initConfig],
    [dtfChannelAtom, value.channel]
  ]);

  // Sync updates when values change
  const setInitConfig = useSetAtom(dtfInitConfigAtom);
  const setChannel = useSetAtom(dtfChannelAtom);

  useEffect(() => {
    setInitConfig(value.initConfig);
    setChannel(value.channel);
  }, [setInitConfig, setChannel, value.channel, value.initConfig]);

  return children;
}

export function useDtfConfig() {
  const initConfig = useAtomValue(dtfInitConfigAtom);
  if (initConfig === null) {
    throw new Error("DtfChannelProvider is missing");
  }
  return initConfig;
}

export function useDtfChannel() {
  const channel = useAtomValue(dtfChannelAtom);
  if (channel === null) {
    throw new Error("DtfChannelProvider is missing");
  }
  return channel;
}
